import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  Calendar, Download, FileText, Filter, Users, Palmtree, 
  Search, ChevronDown, Printer, Clock, CalendarDays
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { companyApi } from '../../../lib/api';
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval, isBefore, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const LEAVE_TYPES = [
  { value: 'vacation', label: 'Congés payés', color: 'bg-green-100 text-green-700', colorHex: '#22c55e' },
  { value: 'sick', label: 'Maladie', color: 'bg-red-100 text-red-700', colorHex: '#ef4444' },
  { value: 'training', label: 'Formation', color: 'bg-blue-100 text-blue-700', colorHex: '#3b82f6' },
  { value: 'personal', label: 'Personnel', color: 'bg-purple-100 text-purple-700', colorHex: '#a855f7' },
  { value: 'other', label: 'Autre', color: 'bg-gray-100 text-gray-700', colorHex: '#6b7280' },
];

const PERIOD_FILTERS = [
  { value: 'all', label: 'Toutes les périodes' },
  { value: 'current_month', label: 'Ce mois' },
  { value: 'current_year', label: 'Cette année' },
  { value: 'past', label: 'Passés uniquement' },
  { value: 'upcoming', label: 'À venir uniquement' },
  { value: 'custom', label: 'Période personnalisée' },
];

export function LeaveHistoryPage() {
  const [leaves, setLeaves] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [selectedCollaborator, setSelectedCollaborator] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current_year');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      const [leavesRes, collabRes] = await Promise.all([
        companyApi.getAllLeaves(),
        companyApi.getCollaborators()
      ]);
      setLeaves(leavesRes.data || []);
      setCollaborators(collabRes.data.filter(c => c.is_active) || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getCollaboratorName = (collabId) => {
    const collab = collaborators.find(c => c.id === collabId);
    return collab?.name || 'Inconnu';
  };

  const getLeaveTypeConfig = (type) => LEAVE_TYPES.find(t => t.value === type) || LEAVE_TYPES[4];

  const calculateDays = (start, end) => {
    return differenceInDays(parseISO(end), parseISO(start)) + 1;
  };

  // Filter leaves based on criteria
  const filteredLeaves = useMemo(() => {
    let result = [...leaves];

    // Filter by collaborator
    if (selectedCollaborator !== 'all') {
      result = result.filter(l => l.collaborator_id === selectedCollaborator);
    }

    // Filter by type
    if (selectedType !== 'all') {
      result = result.filter(l => l.leave_type === selectedType);
    }

    // Filter by period
    const now = new Date();
    if (selectedPeriod === 'current_month') {
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      result = result.filter(l => {
        const leaveStart = parseISO(l.start_date);
        const leaveEnd = parseISO(l.end_date);
        return isWithinInterval(leaveStart, { start: monthStart, end: monthEnd }) ||
               isWithinInterval(leaveEnd, { start: monthStart, end: monthEnd }) ||
               (isBefore(leaveStart, monthStart) && isAfter(leaveEnd, monthEnd));
      });
    } else if (selectedPeriod === 'current_year') {
      const yearStart = startOfYear(now);
      const yearEnd = endOfYear(now);
      result = result.filter(l => {
        const leaveStart = parseISO(l.start_date);
        const leaveEnd = parseISO(l.end_date);
        return isWithinInterval(leaveStart, { start: yearStart, end: yearEnd }) ||
               isWithinInterval(leaveEnd, { start: yearStart, end: yearEnd });
      });
    } else if (selectedPeriod === 'past') {
      result = result.filter(l => isBefore(parseISO(l.end_date), now));
    } else if (selectedPeriod === 'upcoming') {
      result = result.filter(l => isAfter(parseISO(l.start_date), now) || isWithinInterval(now, { start: parseISO(l.start_date), end: parseISO(l.end_date) }));
    } else if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
      const filterStart = parseISO(customStartDate);
      const filterEnd = parseISO(customEndDate);
      result = result.filter(l => {
        const leaveStart = parseISO(l.start_date);
        const leaveEnd = parseISO(l.end_date);
        return isWithinInterval(leaveStart, { start: filterStart, end: filterEnd }) ||
               isWithinInterval(leaveEnd, { start: filterStart, end: filterEnd }) ||
               (isBefore(leaveStart, filterStart) && isAfter(leaveEnd, filterEnd));
      });
    }

    // Filter by search query (reason)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(l => 
        l.reason?.toLowerCase().includes(query) ||
        getCollaboratorName(l.collaborator_id).toLowerCase().includes(query)
      );
    }

    // Sort by start date (most recent first)
    result.sort((a, b) => parseISO(b.start_date) - parseISO(a.start_date));

    return result;
  }, [leaves, selectedCollaborator, selectedType, selectedPeriod, customStartDate, customEndDate, searchQuery, collaborators]);

  // Statistics
  const stats = useMemo(() => {
    const totalDays = filteredLeaves.reduce((sum, l) => sum + calculateDays(l.start_date, l.end_date), 0);
    const byType = LEAVE_TYPES.map(type => ({
      ...type,
      count: filteredLeaves.filter(l => l.leave_type === type.value).length,
      days: filteredLeaves.filter(l => l.leave_type === type.value).reduce((sum, l) => sum + calculateDays(l.start_date, l.end_date), 0)
    }));
    const byCollab = collaborators.map(collab => ({
      id: collab.id,
      name: collab.name,
      count: filteredLeaves.filter(l => l.collaborator_id === collab.id).length,
      days: filteredLeaves.filter(l => l.collaborator_id === collab.id).reduce((sum, l) => sum + calculateDays(l.start_date, l.end_date), 0)
    })).filter(c => c.count > 0).sort((a, b) => b.days - a.days);

    return { totalDays, byType, byCollab, totalLeaves: filteredLeaves.length };
  }, [filteredLeaves, collaborators]);

  // Export to CSV
  const exportCSV = () => {
    const headers = ['Collaborateur', 'Type', 'Date début', 'Date fin', 'Jours', 'Motif'];
    const rows = filteredLeaves.map(l => [
      getCollaboratorName(l.collaborator_id),
      getLeaveTypeConfig(l.leave_type).label,
      format(parseISO(l.start_date), 'dd/MM/yyyy'),
      format(parseISO(l.end_date), 'dd/MM/yyyy'),
      calculateDays(l.start_date, l.end_date),
      l.reason || ''
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique_conges_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('Historique des Congés & Absences', 14, 20);
    
    // Period info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    let periodText = 'Période : ';
    if (selectedPeriod === 'all') periodText += 'Toutes les périodes';
    else if (selectedPeriod === 'current_month') periodText += format(new Date(), 'MMMM yyyy', { locale: fr });
    else if (selectedPeriod === 'current_year') periodText += format(new Date(), 'yyyy');
    else if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
      periodText += `${format(parseISO(customStartDate), 'dd/MM/yyyy')} - ${format(parseISO(customEndDate), 'dd/MM/yyyy')}`;
    }
    doc.text(periodText, 14, 28);
    doc.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}`, 14, 34);
    
    // Summary stats
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text(`Total : ${stats.totalLeaves} période(s) - ${stats.totalDays} jour(s)`, 14, 44);
    
    // Table
    const tableData = filteredLeaves.map(l => [
      getCollaboratorName(l.collaborator_id),
      getLeaveTypeConfig(l.leave_type).label,
      format(parseISO(l.start_date), 'dd/MM/yyyy'),
      format(parseISO(l.end_date), 'dd/MM/yyyy'),
      calculateDays(l.start_date, l.end_date).toString(),
      l.reason || '-'
    ]);

    doc.autoTable({
      startY: 50,
      head: [['Collaborateur', 'Type', 'Début', 'Fin', 'Jours', 'Motif']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 25 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 15, halign: 'center' },
        5: { cellWidth: 'auto' }
      }
    });

    // Summary by collaborator
    if (stats.byCollab.length > 0) {
      const finalY = doc.lastAutoTable.finalY || 50;
      doc.setFontSize(12);
      doc.text('Récapitulatif par collaborateur', 14, finalY + 15);
      
      doc.autoTable({
        startY: finalY + 20,
        head: [['Collaborateur', 'Nb périodes', 'Total jours']],
        body: stats.byCollab.map(c => [c.name, c.count.toString(), c.days.toString()]),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        styles: { fontSize: 9 },
        columnStyles: {
          1: { halign: 'center' },
          2: { halign: 'center' }
        }
      });
    }

    doc.save(`historique_conges_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Rapport PDF généré');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Historique Congés & Absences</h1>
          <p className="text-muted-foreground mt-1">Consultez et exportez l'historique des absences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button className="rounded-full" onClick={exportPDF}>
            <FileText className="w-4 h-4 mr-2" />
            Rapport PDF
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Palmtree className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalLeaves}</p>
                <p className="text-xs text-muted-foreground">Période(s)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalDays}</p>
                <p className="text-xs text-muted-foreground">Jour(s) total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.byCollab.length}</p>
                <p className="text-xs text-muted-foreground">Collaborateur(s)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats.byCollab.length > 0 ? Math.round(stats.totalDays / stats.byCollab.length) : 0}
                </p>
                <p className="text-xs text-muted-foreground">Moy. jours/pers.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Type breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Répartition par type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {stats.byType.filter(t => t.count > 0).map(type => (
              <div key={type.value} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30">
                <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
                <span className="text-sm font-medium">{type.count} ({type.days}j)</span>
              </div>
            ))}
            {stats.byType.every(t => t.count === 0) && (
              <p className="text-sm text-muted-foreground">Aucune donnée pour cette période</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Collaborateur</Label>
              <Select value={selectedCollaborator} onValueChange={setSelectedCollaborator}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {collaborators.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Période</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_FILTERS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedPeriod === 'custom' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Du</Label>
                  <Input 
                    type="date" 
                    className="h-9"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Au</Label>
                  <Input 
                    type="date" 
                    className="h-9"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                  />
                </div>
              </>
            )}
            {selectedPeriod !== 'custom' && (
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs">Recherche</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    className="h-9 pl-8"
                    placeholder="Nom ou motif..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredLeaves.length === 0 ? (
            <div className="p-8 text-center">
              <Palmtree className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Aucun congé trouvé pour ces critères</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead className="text-center">Jours</TableHead>
                  <TableHead>Motif</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeaves.map(leave => {
                  const typeConfig = getLeaveTypeConfig(leave.leave_type);
                  const days = calculateDays(leave.start_date, leave.end_date);
                  const isPast = isBefore(parseISO(leave.end_date), new Date());
                  
                  return (
                    <TableRow key={leave.id} className={isPast ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                            {getCollaboratorName(leave.collaborator_id).charAt(0)}
                          </div>
                          <span className="font-medium">{getCollaboratorName(leave.collaborator_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${typeConfig.color} text-xs`}>
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {format(parseISO(leave.start_date), 'd MMM', { locale: fr })}
                          {leave.start_date !== leave.end_date && (
                            <> → {format(parseISO(leave.end_date), 'd MMM yyyy', { locale: fr })}</>
                          )}
                          {leave.start_date === leave.end_date && (
                            <> {format(parseISO(leave.start_date), 'yyyy')}</>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{days}j</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {leave.reason || '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Summary by collaborator */}
      {stats.byCollab.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              Récapitulatif par collaborateur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {stats.byCollab.map(collab => (
                <div 
                  key={collab.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/20"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {collab.name.charAt(0)}
                    </div>
                    <span className="font-medium">{collab.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{collab.days}j</p>
                    <p className="text-xs text-muted-foreground">{collab.count} période(s)</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
